/**
 * Resolve catalog CategoryMeta IDs by name so product browse can filter
 * `productsMeta` without hardcoding UUIDs (Wallpaper, Flooring, …).
 */

export type CategoryMetaNode = {
  id: string;
  name?: string | null;
  label?: string | null;
  children?: Array<CategoryMetaNode | null> | null;
};

/** Names used in EN / SV catalog trees for the Wallpaper leaf category. */
const WALLPAPER_CATEGORY_NAMES = new Set([
  "wallpaper",
  "tapet",
  "tapeter",
]);

/** Names used in EN / SV catalog trees for the Flooring leaf category. */
const FLOORING_CATEGORY_NAMES = new Set([
  "flooring",
  "golv",
  "golvbeläggning",
  "golvbelaggning",
]);

function normalizeCategoryName(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Collect every CategoryMeta node whose name/label is in `names`,
 * including nested children. Returns ids suitable for `categoryIds` filters.
 */
export function findCategoryIdsByNames(
  roots: ReadonlyArray<CategoryMetaNode | null | undefined> | null | undefined,
  names: ReadonlySet<string>,
): string[] {
  if (!roots?.length || names.size === 0) return [];

  const ids: string[] = [];
  const visit = (node: CategoryMetaNode | null | undefined) => {
    if (!node?.id) return;
    if (
      names.has(normalizeCategoryName(node.name)) ||
      names.has(normalizeCategoryName(node.label))
    ) {
      ids.push(node.id);
    }
    node.children?.forEach((child) => visit(child));
  };

  roots.forEach((root) => visit(root));
  return Array.from(new Set(ids));
}

/**
 * Collect Wallpaper / Tapet category ids from the CategoryMeta tree.
 */
export function findWallpaperCategoryIds(
  roots: ReadonlyArray<CategoryMetaNode | null | undefined> | null | undefined,
): string[] {
  return findCategoryIdsByNames(roots, WALLPAPER_CATEGORY_NAMES);
}

/**
 * Collect Flooring / Golv category ids from the CategoryMeta tree.
 */
export function findFlooringCategoryIds(
  roots: ReadonlyArray<CategoryMetaNode | null | undefined> | null | undefined,
): string[] {
  return findCategoryIdsByNames(roots, FLOORING_CATEGORY_NAMES);
}

export type CategoryMetaOption = {
  id: string;
  name: string;
  label?: string | null;
};

/**
 * Direct children of a top-level CategoryMeta parent (e.g. Color, Pattern),
 * sorted by display name. Used by wallpaper/flooring browse filters.
 */
export function listCategoryMetaChildren(
  roots: ReadonlyArray<CategoryMetaNode | null | undefined> | null | undefined,
  parentName: string,
): CategoryMetaOption[] {
  if (!roots?.length) return [];
  const target = normalizeCategoryName(parentName);
  const parent = roots.find(
    (node) =>
      node &&
      (normalizeCategoryName(node.name) === target ||
        normalizeCategoryName(node.label) === target),
  );
  if (!parent?.children?.length) return [];

  return parent.children
    .filter((child): child is CategoryMetaNode => Boolean(child?.id))
    .map((child) => ({
      id: child.id,
      name: (child.label || child.name || child.id).trim(),
      label: child.label,
    }))
    .filter((child) => child.name.length > 0)
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
}
